import React from 'react';

import { TabContent, TabPane } from 'reactstrap';
import styled, { css, useTheme } from 'styled-components';

import CtaButton from 'components/CtaButton/CtaButton';
import ContentBody from 'components/ModularBlocks/FlexibleTabbed/components/ContentBody';

import { TabsContentProps } from 'interfaces/content-stack/modular-blocks/flexibleTabbed';

import { autoOptimizeImage } from 'utils/functions';
import { getModularBlockLinkData } from 'utils/modularBlockUtils';

const StyledTabPane = styled(TabPane)<{ $reverse?: boolean; $isActive: boolean }>`
  &.active {
    display: flex !important;
    flex-direction: row;
    gap: 5rem;

    @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
      gap: 1rem;
      flex-direction: ${(props) => (props.$reverse ? 'column-reverse' : 'column')};
    }

    @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
      gap: 2rem;
    }
  }

  > div {
    flex: 1;
  }

  &.tab-pane.active {
    animation-name: fadeIn;
    animation-duration: 200ms;
    animation-timing-function: linear;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const StyledTabContent = styled(TabContent)`
  padding-top: 4rem;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    padding-top: 3rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding-top: 2rem;
  }
`;

const StyledImage = styled.img<{ $boxShadow: boolean }>`
  box-shadow: ${(props) =>
    props.$boxShadow ? `0 0.438rem 0.75rem 0.125rem ${props.theme.colors.secondaryBoxShadowGray}` : 'none'};
  width: 100%;
  object-fit: cover;
  aspect-ratio: 3 / 2;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    aspect-ratio: 16 / 9;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    aspect-ratio: 3 / 2;
  }
`;

const CtaButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 3rem;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;

    > *:not([class*='LinkButton']) {
      width: 100%;
    }
  }
`;

const Header = styled.h1(
  (props) => css`
    color: ${props.theme.colors.black};
    font-family: ${props.theme.fonts.fontFamilySecondary};
    margin-bottom: 1.25rem;
    font-size: 1.5rem;

    @media (max-width: ${props.theme.breakpoints.lg}) {
      font-size: 1.25rem;
    }
  `
);

const CopyContainer = styled.div`
  &.text-wrapper {
    padding-top: 2rem;
  }
`;

const StyledVideo = styled.video`
  width: 100%;
  object-fit: cover;
  aspect-ratio: 3 / 2;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    aspect-ratio: 16 / 9;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    aspect-ratio: 3 / 2;
  }
`;

const TabsContent: React.FC<TabsContentProps> = ({ tabs, activeTab }) => {
  const theme = useTheme();
  const videoExt = (video?: { url: string }) => video?.url.split('.').pop() ?? null;

  return (
    <StyledTabContent activeTab={activeTab}>
      {tabs.map((tab, index) => (
        <StyledTabPane $isActive={activeTab === index} tabId={index} key={index} $reverse={tab?.reverseOrder}>
          {tab.tabColumns.map((item, contentIndex) => (
            <div key={contentIndex}>
              {item.blockImage?.image && (
                <StyledImage
                  $boxShadow={item.blockImage.dropshadow}
                  src={autoOptimizeImage(item.blockImage.image.url)}
                  alt={item.blockImage.imageAltText}
                />
              )}
              {item.blockVideo && (
                <StyledVideo autoPlay={true} loop={true} muted={true} playsInline={true}>
                  <source src={item.blockVideo?.url} type={`video/${videoExt(item.blockVideo)}`} />
                </StyledVideo>
              )}
              <CopyContainer className={item.cssClassname}>
                {item.header && <Header>{item.header}</Header>}
                {item.body && <ContentBody richText={item.body} />}
              </CopyContainer>
              {item.ctaButtons?.length > 0 && (
                <CtaButtonContainer>
                  {item.ctaButtons.map((button, buttonIndex) => {
                    const { link, openInNewWindow } = getModularBlockLinkData(button.buttonLink);

                    return (
                      <CtaButton
                        to={link}
                        openInNewWindow={openInNewWindow}
                        key={buttonIndex}
                        buttonType={button.buttonType}
                        text={button.buttonText}
                        textColor={button.buttonType == 'primary' ? theme.colors.white : theme.colors.black}
                        dataLayerInfo={item.header}
                      />
                    );
                  })}
                </CtaButtonContainer>
              )}
            </div>
          ))}
        </StyledTabPane>
      ))}
    </StyledTabContent>
  );
};

export default TabsContent;
